import { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  fetchAdmins,
  deleteAdmin,
  Admin,
} from '../../../api/ApiAdmin';
import { UserIcon, TrashIcon, ShieldCheck, AlertCircle, X } from 'lucide-react';
import Notification from './Notificationnn';
import { useTranslation } from '../../../../TranslationContext';

interface AdminListProps {
  companyId: string | null;
  currentAdmin: Admin | null;
}

// Fonction pour identifier un super admin
const isSuperAdmin = (admin: Admin | null): boolean => {
  return admin?.default ?? false;
};

// Interface pour le modal de confirmation
interface ConfirmDeleteModalProps {
  isOpen: boolean;
  admin: Admin | null;
  onConfirm: () => void;
  onCancel: () => void;
}

// Composant pour la modale de confirmation de suppression
const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({ isOpen, admin, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  
  if (!isOpen || !admin) return null;
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Overlay semi-transparent */}
        <div className="fixed inset-0 transition-opacity bg-slate-500 bg-opacity-50" aria-hidden="true" onClick={onCancel}></div>
        
        {/* Centrer la boîte de dialogue */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        
        {/* Modal panel */}
        <div className="inline-block overflow-hidden text-left align-bottom transition-all transform bg-white rounded-xl shadow-lg sm:my-8 sm:align-middle sm:max-w-lg sm:w-full relative">
          {/* Bouton de fermeture */}
          <button 
            onClick={onCancel}
            className="absolute top-3 right-3 text-slate-400 hover:text-slate-500 focus:outline-none"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="px-4 pt-5 pb-4 bg-white sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-rose-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
                <AlertCircle className="w-6 h-6 text-rose-600" aria-hidden="true" />
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="text-lg font-medium leading-6 text-slate-800" id="modal-title">
                  {t('confirm_delete_admin_title')}
                </h3>
                <div className="mt-4">
                  <div className="flex items-center p-4 mb-4 bg-slate-50 rounded-lg border border-slate-100">
                    <div className="flex-shrink-0 w-10 h-10 bg-sky-100 rounded-full flex items-center justify-center mr-4">
                      <UserIcon className="w-6 h-6 text-sky-600" />
                    </div>
                    <div>
                      <div className="font-medium text-slate-800">{admin.fullName || admin.email.split('@')[0]}</div>
                      <div className="text-sm text-slate-500">{admin.email}</div>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600">
                    {t('confirm_delete_admin_message', { 
                      name: admin.fullName || admin.email.split('@')[0] 
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="px-4 py-4 bg-slate-50 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
            <button
              type="button"
              className="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-rose-600 border border-transparent rounded-lg shadow-md hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500 sm:w-auto sm:text-sm transition-colors duration-200"
              onClick={onConfirm}
            >
              {t('delete')}
            </button>
            <button
              type="button"
              className="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-slate-700 bg-white border border-slate-300 rounded-lg shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 sm:mt-0 sm:w-auto sm:text-sm transition-colors duration-200"
              onClick={onCancel}
            >
              {t('cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminItem: React.FC<{
  admin: Admin;
  companyId: string | null;
  currentAdmin: Admin | null;
  onDelete: (admin: Admin) => void;
}> = ({ admin, currentAdmin, onDelete }) => {
  const { t } = useTranslation();
  const isSuperAdminCurrent = isSuperAdmin(currentAdmin);
  const isThisAdminSuperAdmin = admin.default ?? false;

  if (import.meta.env.DEV) {
    console.log(`AdminItem for ${admin.email}:`, {
      isSuperAdmin: isSuperAdminCurrent,
      currentAdmin,
      isThisAdminSuperAdmin,
    });
  }

  return (
    <li key={admin.id} className="px-4 py-4 sm:px-6">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center">
          <div
            className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${
              isThisAdminSuperAdmin ? 'bg-purple-100' : 'bg-sky-100'
            }`}
          >
            <UserIcon className={`h-6 w-6 ${isThisAdminSuperAdmin ? 'text-purple-600' : 'text-sky-600'}`} />
          </div>
          <div className="ml-4">
            <div className="flex items-center">
              <div className="text-sm font-medium text-slate-800">{admin.fullName || admin.email.split('@')[0]}</div>
              {isThisAdminSuperAdmin && (
                <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                  {t('super_admin')}
                </span>
              )}
            </div>
            <div className="text-sm text-slate-500">{admin.email}</div>
          </div>
        </div>

        <div className="flex items-center space-x-2 flex-wrap justify-center sm:justify-end">
          {isThisAdminSuperAdmin ? (
            <div className="px-3 py-2 border border-slate-200 rounded-md text-sm text-slate-500 bg-slate-50">
              <div className="flex items-center">
                <ShieldCheck className="h-4 w-4 mr-2 text-purple-600" />
                {t('full_privileges')}
              </div>
            </div>
          ) : (
            <>
              {!isThisAdminSuperAdmin && isSuperAdminCurrent && admin.id !== currentAdmin?.id && (
                <div className="relative group">
                  <button
                    onClick={() => onDelete(admin)}
                    className="inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-rose-600 hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500 transition-colors"
                    title={t('delete_administrator')}
                  >
                    <TrashIcon className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <span className="absolute bottom-full mb-2 hidden group-hover:block text-xs bg-slate-800 text-white px-2 py-1 rounded">
                    {t('action_reserved_for_super_admins')}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </li>
  );
};

const AdminList: React.FC<AdminListProps> = ({ companyId, currentAdmin }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
    isVisible: boolean;
  } | null>(null);
  
  // État pour la modale de confirmation de suppression
  const [deleteModalState, setDeleteModalState] = useState<{
    isOpen: boolean;
    adminToDelete: Admin | null;
  }>({
    isOpen: false,
    adminToDelete: null,
  });

  if (import.meta.env.DEV) {
    console.log('AdminList:', { currentAdmin, companyId });
  }

  const { data: admins = [], isLoading: isLoadingAdmins } = useQuery<Admin[]>(
    ['admins', companyId],
    () => fetchAdmins(companyId || ''),
    {
      enabled: !!companyId,
      retry: false,
      onError: (error) => {
        console.error('Error loading administrators:', error);
        showNotification('error', t('error_loading_administrators'));
      },
    }
  );

  const sortedAdmins = useMemo(() => {
    if (!admins) return [];
    return [...admins].sort((a, b) => {
      if ((a.default ?? false) && !(b.default ?? false)) return -1;
      if (!(a.default ?? false) && (b.default ?? false)) return 1;
      return 0;
    });
  }, [admins]);

  const deleteMutation = useMutation(
    ({ adminId, companyId }: { adminId: string; companyId: string }) => deleteAdmin(adminId, companyId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['admins', companyId]);
        showNotification('success', t('administrator_deleted_successfully'));
      },
      onError: (error) => {
        console.error("Error deleting administrator:", error);
        showNotification('error', t('error_deleting_administrator'));
      },
    }
  );

  const showNotification = useCallback((type: 'success' | 'error', message: string) => {
    setNotification({ type, message, isVisible: true });
    setTimeout(() => {
      setNotification((prev) => (prev ? { ...prev, isVisible: false } : null));
    }, 3000);
  }, []);

  const handleDeleteAdmin = useCallback(
    (admin: Admin) => {
      if (!companyId) {
        showNotification('error', t('company_id_missing'));
        return;
      }
      if (!isSuperAdmin(currentAdmin)) {
        showNotification('error', t('only_super_admin_can_delete'));
        return;
      }
      if (admin.id === currentAdmin?.id) {
        showNotification('error', t('cannot_delete_own_account'));
        return;
      }
      
      // Ouvrir la modale de confirmation au lieu d'utiliser window.confirm
      setDeleteModalState({
        isOpen: true,
        adminToDelete: admin,
      });
    },
    [companyId, currentAdmin, showNotification, t]
  );
  
  // Confirmer la suppression
  const confirmDelete = useCallback(() => {
    if (deleteModalState.adminToDelete && companyId) {
      deleteMutation.mutate({ 
        adminId: deleteModalState.adminToDelete.id, 
        companyId 
      });
      setDeleteModalState({ isOpen: false, adminToDelete: null });
    }
  }, [companyId, deleteMutation, deleteModalState.adminToDelete]);
  
  // Annuler la suppression
  const cancelDelete = useCallback(() => {
    setDeleteModalState({ isOpen: false, adminToDelete: null });
  }, []);

  if (isLoadingAdmins) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-500"></div>
        <span className="ml-3 text-sky-600">{t('loading_administrators')}</span>
      </div>
    );
  }

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg">
      {notification && (
        <div className="fixed top-4 right-4 z-50">
          <Notification type={notification.type} message={notification.message} isVisible={notification.isVisible} />
        </div>
      )}
      
      {/* Modale de confirmation de suppression */}
      <ConfirmDeleteModal 
        isOpen={deleteModalState.isOpen}
        admin={deleteModalState.adminToDelete}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />

      <div className="px-4 py-5 border-b border-slate-200 sm:px-6">
        <h3 className="text-lg leading-6 font-medium text-slate-800">{t('administrators')}</h3>
        <p className="mt-1 max-w-2xl text-sm text-slate-500">
          {isSuperAdmin(currentAdmin)
            ? t('manage_administrators')
            : t('view_administrators')}
        </p>
      </div>

      {sortedAdmins.length > 0 ? (
        <>
          <ul className="divide-y divide-slate-200">
            {sortedAdmins.map((admin) => (
              <AdminItem
                key={admin.id}
                admin={admin}
                companyId={companyId}
                currentAdmin={currentAdmin}
                onDelete={handleDeleteAdmin}
              />
            ))}
          </ul>
          <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 text-sm text-slate-600 text-right">
            {t('admin_count', { count: sortedAdmins.length })}
          </div>
        </>
      ) : (
        <div className="text-center py-8">
          <p className="text-slate-500">
            {companyId
              ? t('no_administrators_found')
              : t('select_company_to_view_admins')}
          </p>
        </div>
      )}
    </div>
  );
};

export default AdminList;

// import { useState, useCallback, useMemo } from 'react';
// import { useQuery, useMutation, useQueryClient } from 'react-query';
// import {
//   fetchAdmins,
//   deleteAdmin,
//   Admin,
//   fetchCompanyPermissions,
//   fetchApplications,
//   assignPermission,
//   getAdminPermissions,
//   revokePermission,
//   Permission,
//   Application,
//   AdminPermission,
// } from './ApiAdmin';
// import { UserIcon, TrashIcon, ShieldCheck } from 'lucide-react';
// import Notification from './Notificationnn';
// import PermissionDropdown from './PermissionDropdown';
// import ApplicationDropdown from './ApplicationDropdown';

// interface AdminListProps {
//   companyId: string | null;
//   currentAdmin: Admin | null;
// }

// // Fonction pour identifier un super admin
// const isSuperAdmin = (admin: Admin | null): boolean => {
//   return admin?.default ?? false;
// };

// const AdminItem: React.FC<{
//   admin: Admin;
//   permissions: Permission[];
//   applications: Application[];
//   companyId: string | null;
//   currentAdmin: Admin | null;
//   onDelete: (admin: Admin) => void;
//   onTogglePermission: (adminId: string, permissionId: string, isAssigned: boolean) => void;
//   onToggleApplicationPermission: (adminId: string, applicationId: string, isAssigned: boolean) => void;
// }> = ({ admin, permissions, applications, companyId, currentAdmin, onDelete, onTogglePermission, onToggleApplicationPermission }) => {
//   const {
//     data: adminPermissionsRaw = [],
//     isLoading: isLoadingAdminPermissions,
//   } = useQuery<Permission[]>(
//     ['adminPermissions', admin.id, companyId],
//     () => getAdminPermissions(companyId || '', admin.id),
//     {
//       enabled: !!admin.id && !!companyId,
//       retry: false,
//       onError: (error) => {
//         console.error("Erreur lors du chargement des permissions de l'administrateur:", error);
//       },
//     }
//   );

//   const adminPermissions: AdminPermission[] = adminPermissionsRaw.map((perm) => ({
//     adminId: admin.id,
//     permissionId: perm.id,
//     applicationId: undefined,
//   }));

//   const isSuperAdminCurrent = isSuperAdmin(currentAdmin);
//   const isThisAdminSuperAdmin = admin.default ?? false;
//   const readOnly = !isSuperAdminCurrent;

//   if (import.meta.env.DEV) {
//     console.log(`AdminItem for ${admin.email}:`, {
//       isSuperAdmin: isSuperAdminCurrent,
//       readOnly,
//       currentAdmin,
//       isThisAdminSuperAdmin,
//       adminPermissionsCount: adminPermissions.length,
//     });
//   }

//   return (
//     <li key={admin.id} className="px-4 py-4 sm:px-6">
//       <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
//         <div className="flex items-center">
//           <div
//             className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${
//               isThisAdminSuperAdmin ? 'bg-purple-100' : 'bg-indigo-100'
//             }`}
//           >
//             <UserIcon className={`h-6 w-6 ${isThisAdminSuperAdmin ? 'text-purple-600' : 'text-indigo-600'}`} />
//           </div>
//           <div className="ml-4">
//             <div className="flex items-center">
//               <div className="text-sm font-medium text-gray-900">{admin.fullName || admin.email.split('@')[0]}</div>
//               {isThisAdminSuperAdmin && (
//                 <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
//                   Super Admin
//                 </span>
//               )}
//             </div>
//             <div className="text-sm text-gray-500">{admin.email}</div>
//           </div>
//         </div>

//         <div className="flex items-center space-x-2 flex-wrap justify-center sm:justify-end">
//           {isThisAdminSuperAdmin ? (
//             <div className="px-3 py-2 border border-gray-200 rounded-md text-sm text-gray-500 bg-gray-50">
//               <div className="flex items-center">
//                 <ShieldCheck className="h-4 w-4 mr-2 text-purple-600" />
//                 Privilèges complets
//               </div>
//             </div>
//           ) : (
//             <>
//               <PermissionDropdown
//                 admin={admin}
//                 permissions={permissions}
//                 adminPermissions={adminPermissions}
//                 onTogglePermission={(permissionId, isAssigned) => onTogglePermission(admin.id, permissionId, isAssigned)}
//                 loading={isLoadingAdminPermissions}
//                 readOnly={readOnly}
//               />
//               <ApplicationDropdown
//                 admin={admin}
//                 applications={applications}
//                 adminPermissions={adminPermissions}
//                 onToggleApplication={(applicationId, isAssigned) =>
//                   onToggleApplicationPermission(admin.id, applicationId, isAssigned)
//                 }
//                 loading={isLoadingAdminPermissions}
//                 readOnly={readOnly}
//               />
//               {!isThisAdminSuperAdmin && isSuperAdminCurrent && admin.id !== currentAdmin?.id && (
//                 <div className="relative group">
//                   <button
//                     onClick={() => onDelete(admin)}
//                     className="inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
//                     title="Supprimer l'administrateur"
//                   >
//                     <TrashIcon className="h-4 w-4" aria-hidden="true" />
//                   </button>
//                   <span className="absolute bottom-full mb-2 hidden group-hover:block text-xs bg-gray-800 text-white px-2 py-1 rounded">
//                     Action réservée aux super administrateurs
//                   </span>
//                 </div>
//               )}
//             </>
//           )}
//         </div>
//       </div>
//     </li>
//   );
// };

// const AdminList: React.FC<AdminListProps> = ({ companyId, currentAdmin }) => {
//   const queryClient = useQueryClient();
//   const [notification, setNotification] = useState<{
//     type: 'success' | 'error';
//     message: string;
//     isVisible: boolean;
//   } | null>(null);

//   if (import.meta.env.DEV) {
//     console.log('AdminList:', { currentAdmin, companyId });
//   }

//   const { data: admins = [], isLoading: isLoadingAdmins } = useQuery<Admin[]>(
//     ['admins', companyId],
//     () => fetchAdmins(companyId || ''),
//     {
//       enabled: !!companyId,
//       retry: false,
//       onError: (error) => {
//         console.error('Erreur lors du chargement des administrateurs:', error);
//         showNotification('error', 'Erreur lors du chargement des administrateurs');
//       },
//     }
//   );

//   const { data: permissions = [] } = useQuery<Permission[]>(
//     ['permissions', companyId],
//     () => fetchCompanyPermissions(companyId || ''),
//     {
//       enabled: !!companyId,
//       retry: false,
//       onError: (error) => {
//         console.error('Erreur lors du chargement des permissions:', error);
//         showNotification('error', 'Erreur lors du chargement des permissions');
//       },
//     }
//   );

//   const { data: applications = [] } = useQuery<Application[]>(
//     ['applications', companyId],
//     () => (companyId ? fetchApplications(companyId) : []),
//     {
//       enabled: !!companyId,
//       retry: false,
//       onError: (error) => {
//         console.error('Erreur lors du chargement des applications:', error);
//       },
//     }
//   );

//   const sortedAdmins = useMemo(() => {
//     if (!admins) return [];
//     return [...admins].sort((a, b) => {
//       if ((a.default ?? false) && !(b.default ?? false)) return -1;
//       if (!(a.default ?? false) && (b.default ?? false)) return 1;
//       return 0;
//     });
//   }, [admins]);

//   const deleteMutation = useMutation(
//     ({ adminId, companyId }: { adminId: string; companyId: string }) => deleteAdmin(adminId, companyId),
//     {
//       onSuccess: () => {
//         queryClient.invalidateQueries(['admins', companyId]);
//         showNotification('success', 'Administrateur supprimé avec succès');
//       },
//       onError: (error) => {
//         console.error("Erreur lors de la suppression de l'administrateur:", error);
//         showNotification('error', "Erreur lors de la suppression de l'administrateur");
//       },
//     }
//   );

//   const assignPermissionMutation = useMutation(
//     ({ companyId, adminId, permissionId }: { companyId: string; adminId: string; permissionId: string }) =>
//       assignPermission(companyId, adminId, permissionId),
//     {
//       onSuccess: (_, variables) => {
//         queryClient.invalidateQueries(['adminPermissions', variables.adminId, companyId]);
//         showNotification('success', 'Permission attribuée avec succès');
//       },
//       onError: (error) => {
//         console.error("Erreur lors de l'attribution de la permission:", error);
//         showNotification('error', "Erreur lors de l'attribution de la permission");
//       },
//     }
//   );

//   const revokePermissionMutation = useMutation(
//     ({ companyId, adminId, permissionId }: { companyId: string; adminId: string; permissionId: string }) =>
//       revokePermission(companyId, adminId, permissionId),
//     {
//       onSuccess: (_, variables) => {
//         queryClient.invalidateQueries(['adminPermissions', variables.adminId, companyId]);
//         showNotification('success', 'Permission révoquée avec succès');
//       },
//       onError: (error) => {
//         console.error("Erreur lors de la révocation de la permission:", error);
//         showNotification('error', "Erreur lors de la révocation de la permission");
//       },
//     }
//   );

//   const showNotification = useCallback((type: 'success' | 'error', message: string) => {
//     setNotification({ type, message, isVisible: true });
//     setTimeout(() => {
//       setNotification((prev) => (prev ? { ...prev, isVisible: false } : null));
//     }, 3000);
//   }, []);

//   const handleDeleteAdmin = useCallback(
//     (admin: Admin) => {
//       if (!companyId) {
//         showNotification('error', 'Identifiant de l\'entreprise manquant');
//         return;
//       }
//       if (!isSuperAdmin(currentAdmin)) {
//         showNotification('error', 'Seul un super administrateur peut supprimer des administrateurs');
//         return;
//       }
//       if (admin.id === currentAdmin?.id) {
//         showNotification('error', 'Vous ne pouvez pas supprimer votre propre compte');
//         return;
//       }
//       const confirmDelete = window.confirm(
//         `Êtes-vous sûr de vouloir supprimer l'administrateur ${admin.fullName || admin.email} ?`
//       );
//       if (confirmDelete) {
//         deleteMutation.mutate({ adminId: admin.id, companyId });
//       }
//     },
//     [companyId, deleteMutation, currentAdmin, showNotification]
//   );

//   const handleTogglePermission = useCallback(
//     (adminId: string, permissionId: string, isAssigned: boolean) => {
//       if (!companyId) {
//         showNotification('error', 'Identifiant de l\'entreprise manquant');
//         return;
//       }
//       if (!isSuperAdmin(currentAdmin)) {
//         showNotification('error', 'Seul un super administrateur peut modifier les permissions');
//         return;
//       }
//       if (adminId === currentAdmin?.id) {
//         showNotification('error', 'Vous ne pouvez pas modifier vos propres permissions');
//         return;
//       }
//       if (isAssigned) {
//         revokePermissionMutation.mutate({ companyId, adminId, permissionId });
//       } else {
//         assignPermissionMutation.mutate({ companyId, adminId, permissionId });
//       }
//     },
//     [companyId, revokePermissionMutation, assignPermissionMutation, currentAdmin, showNotification]
//   );

//   const handleToggleApplicationPermission = useCallback(
//     (adminId: string, applicationId: string, isAssigned: boolean) => {
//       if (!companyId) {
//         showNotification('error', 'Identifiant de l\'entreprise manquant');
//         return;
//       }
//       if (!isSuperAdmin(currentAdmin)) {
//         showNotification('error', 'Seul un super administrateur peut modifier les permissions d’application');
//         return;
//       }
//       if (adminId === currentAdmin?.id) {
//         showNotification('error', 'Vous ne pouvez pas modifier vos propres permissions');
//         return;
//       }
//       const defaultPermission = permissions.find(
//         (p) => p.name.toLowerCase().includes('read') || p.name.toLowerCase().includes('view')
//       );
//       if (!defaultPermission) {
//         showNotification('error', 'Aucune permission standard trouvée pour l’accès à l’application');
//         return;
//       }
//       if (isAssigned) {
//         const adminPerms = queryClient.getQueryData<Permission[]>(['adminPermissions', adminId, companyId]) || [];
//         const appPerms = adminPerms.filter((p) => p.id === defaultPermission.id);
//         Promise.all(appPerms.map((p) => revokePermission(companyId, adminId, p.id)))
//           .then(() => {
//             queryClient.invalidateQueries(['adminPermissions', adminId, companyId]);
//             showNotification('success', "Accès à l'application révoqué");
//           })
//           .catch(() => {
//             showNotification('error', "Erreur lors de la révocation de l'accès à l'application");
//           });
//       } else {
//         assignPermissionMutation.mutate({
//           companyId,
//           adminId,
//           permissionId: defaultPermission.id,
//         });
//       }
//     },
//     [companyId, permissions, queryClient, assignPermissionMutation, revokePermissionMutation, showNotification, currentAdmin]
//   );

//   if (isLoadingAdmins) {
//     return (
//       <div className="flex justify-center items-center h-64">
//         <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500"></div>
//         <span className="ml-3 text-indigo-600">Chargement des administrateurs...</span>
//       </div>
//     );
//   }

//   return (
//     <div className="bg-white shadow overflow-hidden sm:rounded-lg">
//       {notification && (
//         <div className="fixed top-4 right-4 z-50">
//           <Notification type={notification.type} message={notification.message} isVisible={notification.isVisible} />
//         </div>
//       )}

//       <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
//         <h3 className="text-lg leading-6 font-medium text-gray-900">Administrateurs</h3>
//         <p className="mt-1 max-w-2xl text-sm text-gray-500">
//           {isSuperAdmin(currentAdmin)
//             ? 'Gérez les administrateurs et leurs permissions'
//             : 'Consultez les administrateurs et leurs permissions'}
//         </p>
//       </div>

//       {sortedAdmins.length > 0 ? (
//         <ul className="divide-y divide-gray-200">
//           {sortedAdmins.map((admin) => (
//             <AdminItem
//               key={admin.id}
//               admin={admin}
//               permissions={permissions}
//               applications={applications}
//               companyId={companyId}
//               currentAdmin={currentAdmin}
//               onDelete={handleDeleteAdmin}
//               onTogglePermission={handleTogglePermission}
//               onToggleApplicationPermission={handleToggleApplicationPermission}
//             />
//           ))}
//         </ul>
//       ) : (
//         <div className="text-center py-8">
//           <p className="text-gray-500">
//             {companyId
//               ? 'Aucun administrateur trouvé pour cette entreprise'
//               : 'Veuillez sélectionner une entreprise pour voir ses administrateurs'}
//           </p>
//         </div>
//       )}
//     </div>
//   );
// };

// export default AdminList;